import { Link } from "react-router-dom";
import { Code2, Rocket, Megaphone, PenTool, Check, ArrowUpRight } from "lucide-react";
import BrutalCard from "@/components/common/BrutalCard";
import BrutalButton from "@/components/common/BrutalButton";
import SectionHeading from "@/components/common/SectionHeading";
import CTASection from "@/components/sections/CTASection";

const SERVICES = [
    {
        icon: Code2,
        title: "Software Solutions",
        bg: "yellow",
        copy: "Custom internal tools, dashboards, integrations and platforms — engineered to remove the bottlenecks holding your team back.",
        deliverables: [
            "Discovery & system audit",
            "Architecture blueprint",
            "Production rollout in 6-10 weeks",
            "Hand-off + 30 days of support",
        ],
    },
    {
        icon: Rocket,
        title: "SaaS Products",
        bg: "blue",
        copy: "From zero-to-one MVPs to scaling platforms. Bold UI, sturdy engineering, and a growth-ready foundation baked in from day one.",
        deliverables: [
            "Product strategy + opinionated UI",
            "Full-stack engineering pod",
            "Auth, billing, analytics ready",
            "Launch playbook included",
        ],
    },
    {
        icon: Megaphone,
        title: "Digital Marketing",
        bg: "pink",
        copy: "Performance creative, lifecycle, intent-led SEO, and founder-led social. We don't run campaigns — we build loops that compound.",
        deliverables: [
            "Growth audit + 90-day plan",
            "Channel pods (paid, SEO, lifecycle)",
            "Founder-led content engine",
            "Monthly outcome review",
        ],
    },
    {
        icon: PenTool,
        title: "Brand & UI",
        bg: "green",
        copy: "Distinctive identities and product UI systems. We help brave teams stand out in commodity markets.",
        deliverables: [
            "Brand sprint",
            "Identity system + guidelines",
            "Reusable UI kit (React + Figma)",
            "Marketing asset starter pack",
        ],
    },
];

export default function Services() {
    return (
        <div data-testid="services-page">
            <section className="border-b-4 border-ink py-20 md:py-28">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 text-center">
                    <span className="label-mono inline-block bg-brand-yellow border-2 border-ink rounded-full px-3 py-1">
                        Services
                    </span>
                    <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.92] tracking-tighter">
                        Three studios.{" "}
                        <span className="inline-block bg-brand-blue text-white px-3 -rotate-1 border-2 border-ink shadow-brutal">
                            One pod.
                        </span>
                    </h1>
                    <p className="mt-7 font-body text-ink/70 text-lg leading-relaxed max-w-2xl mx-auto">
                        Pick a single capability or run a full-stack engagement.
                        Either way, you get the same opinionated team and the same
                        weekly cadence.
                    </p>
                </div>
            </section>

            <section className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 space-y-20">
                    {SERVICES.map((s, i) => {
                        const Icon = s.icon;
                        const reverse = i % 2 === 1;
                        return (
                            <div
                                key={s.title}
                                data-testid={`service-block-₹{i}`}
                                className={`grid lg:grid-cols-12 gap-10 items-center ₹{
                                    reverse ? "lg:[direction:rtl]" : ""
                                }`}
                            >
                                <div className="lg:col-span-5 [direction:ltr]">
                                    <BrutalCard bg={s.bg}>
                                        <div className="w-16 h-16 rounded-xl bg-white border-2 border-ink flex items-center justify-center">
                                            <Icon size={28} />
                                        </div>
                                        <h2 className="mt-6 font-display text-4xl md:text-5xl font-black leading-none tracking-tighter">
                                            {s.title}
                                        </h2>
                                        <span className="mt-4 label-mono inline-block">
                                            0{i + 1} / 0{SERVICES.length}
                                        </span>
                                    </BrutalCard>
                                </div>
                                <div className="lg:col-span-7 [direction:ltr]">
                                    <p className="font-body text-lg text-ink/80 leading-relaxed">{s.copy}</p>
                                    <ul className="mt-6 grid sm:grid-cols-2 gap-3">
                                        {s.deliverables.map((d) => (
                                            <li
                                                key={d}
                                                className="flex items-start gap-3 bg-white border-2 border-ink rounded-lg px-4 py-3 shadow-brutal-sm"
                                            >
                                                <span className="w-5 h-5 rounded-full bg-ink text-cream flex items-center justify-center mt-0.5 flex-shrink-0">
                                                    <Check size={12} />
                                                </span>
                                                <span className="font-body text-sm">{d}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <BrutalButton
                                        as={Link}
                                        to="/contact"
                                        size="md"
                                        variant="ink"
                                        className="mt-7"
                                        data-testid={`service-cta-₹{i}`}
                                    >
                                        Talk to us about {s.title}
                                        <ArrowUpRight size={16} />
                                    </BrutalButton>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            <SectionHeading
                eyebrow="Pricing"
                title="Pods, not packages."
                subtitle="We sell time of senior operators. Most engagements run 4-12 weeks at ₹24k-₹72k. Tell us your goal and we'll size it together."
                align="center"
                className="px-6 mb-14"
            />
            <CTASection />
        </div>
    );
}
